/**
 * 누끼 라이브러리 문서 사이트.
 *
 * 예시가 **스크린샷이 아니라 실제로 도는 것**이 핵심이다 —
 * 라이브러리가 깨지면 이 페이지가 깨지므로 문서가 곧 회귀 테스트가 된다.
 */
import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { NukkiEditor, useNukki, NukkiCanvas } from '@nukki/react';
import { remoteBackend } from '@nukki/core';
import type { MaskBackend } from '@nukki/core';
import { SAMPLE_PNG } from './sample.js';
import '@nukki/react/styles.css';

/* ══ 공통 ══════════════════════════════════════════════════ */

const backend: MaskBackend = remoteBackend({ baseUrl: '' });   // 같은 오리진

async function sampleFile(): Promise<File> {
  const blob = await (await fetch(SAMPLE_PNG)).blob();
  return new File([blob], 'sample.png', { type: 'image/png' });
}

function Code({ lang = 'tsx', children }: { lang?: string; children: string }) {
  return (
    <pre className="code">
      <span className="code-lang">{lang}</span>
      <code>{children.trim()}</code>
    </pre>
  );
}

function Section({ id, title, sub, children }: {
  id: string; title: string; sub?: string; children: React.ReactNode;
}) {
  return (
    <section id={id}>
      <h2>{title}</h2>
      {sub && <p className="sub">{sub}</p>}
      {children}
    </section>
  );
}

/** 코드 왼쪽 · 실제 렌더 오른쪽. */
function Live({ code, children, note }: { code: string; children: React.ReactNode; note?: string }) {
  return (
    <div className="live">
      <div className="live-code"><Code>{code}</Code>{note && <p className="note">{note}</p>}</div>
      <div className="live-out">
        <div className="live-badge">실제 렌더</div>
        {children}
      </div>
    </div>
  );
}

/** 샘플 이미지를 자동으로 물려 로드된 상태를 보여주는 에디터. */
function AutoEditor(props: React.ComponentProps<typeof NukkiEditor>) {
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const file = await sampleFile();
      // 문서용 편의: 파일 input 에 샘플을 심어 로드된 화면을 바로 보여준다.
      for (let i = 0; i < 40 && alive; i++) {
        const input = host.current?.querySelector<HTMLInputElement>('input[type=file]');
        if (input) {
          const dt = new DataTransfer();
          dt.items.add(file);
          Object.defineProperty(input, 'files', { value: dt.files, configurable: true });
          input.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
        await new Promise((r) => setTimeout(r, 25));
      }
    })();
    return () => { alive = false; };
  }, []);
  return <div ref={host}><NukkiEditor {...props} /></div>;
}

/* ══ 페이지 ════════════════════════════════════════════════ */

function App() {
  const [health, setHealth] = useState<{ ready: boolean; model: string } | null>(null);
  useEffect(() => {
    fetch('/v1/health').then((r) => r.json()).then(setHealth).catch(() => {});
  }, []);

  return (
    <>
      <header className="hero">
        <h1>누끼 <span>라이브러리</span></h1>
        <p>AI 배경 제거 에디터를 여러 프로젝트에서 재사용하기 위한 패키지 묶음.</p>
        {health && (
          <p className={`status ${health.ready ? 'ok' : 'bad'}`}>
            서버 {health.ready ? '준비됨' : '준비 중'} · 모델 <code>{health.model}</code>
            {health.model === '__fake__' && ' (개발용 가짜 모델 — 품질은 참고만)'}
          </p>
        )}
        <nav>
          {[['packages', '패키지'], ['install', '설치'], ['demo', '데모'],
            ['skin', '스키닝 4단계'], ['rn', 'React Native'], ['api', '서버 API'],
            ['deploy', '배포']].map(([id, label]) => (
            <a key={id} href={`#${id}`}>{label}</a>
          ))}
        </nav>
      </header>

      <main>
        <Section id="packages" title="패키지" sub="소비처에 따라 필요한 것만 설치한다.">
          <table className="grid">
            <thead><tr><th>패키지</th><th>쓰는 곳</th><th>비고</th></tr></thead>
            <tbody>
              <tr>
                <td><code>@nukki/core</code></td>
                <td>웹 편집 엔진</td>
                <td>Canvas 2D. UI 없음. 마스크 연산은 <code>Surface</code> 인자화 → Skia 이식 가능</td>
              </tr>
              <tr>
                <td><code>@nukki/react</code></td>
                <td>React 컴포넌트</td>
                <td>통짜 <code>&lt;NukkiEditor&gt;</code> + headless <code>useNukki()</code></td>
              </tr>
              <tr>
                <td><code>@nukki/client</code></td>
                <td>React Native · Node</td>
                <td><strong>DOM 무의존.</strong> canvas 가 없는 환경</td>
              </tr>
              <tr>
                <td><code>nukki-core</code> (pip)</td>
                <td>서버</td>
                <td>추론 + 가드. 기존 FastAPI 앱에 라우터로 마운트 가능</td>
              </tr>
            </tbody>
          </table>
        </Section>

        <Section id="install" title="설치" sub="프로젝트 성격에 따라 갈린다.">
          <div className="cols">
            <div>
              <h3>웹 (React)</h3>
              <Code lang="bash">{`
npm i @nukki/react @nukki/core
`}</Code>
              <Code>{`
import { NukkiEditor } from '@nukki/react';
import { remoteBackend } from '@nukki/core';
import '@nukki/react/styles.css';
`}</Code>
              <p className="note">
                Vite · CRA 5 · Next 어디서나 동작한다. ESM/CJS 양쪽을 낸다 —
                CRA 5(webpack)에서 ESM 전용 패키지가 말썽인 것을 피하려고 dual 로 배포한다.
              </p>
            </div>
            <div>
              <h3>React Native (Expo)</h3>
              <Code lang="bash">{`
npm i @nukki/client
`}</Code>
              <Code>{`
import { NukkiClient } from '@nukki/client';

const nukki = new NukkiClient({
  baseUrl: NUKKI_BASE_URL,
  headers: { 'X-Nukki-Key': NUKKI_APP_KEY },
});
`}</Code>
              <p className="note">
                RN 에는 canvas 가 없어 <code>@nukki/core</code> 는 못 쓴다.
                서버가 잘라준 투명 이미지를 받는 경로다.
              </p>
            </div>
          </div>
          <h3>서버</h3>
          <Code lang="python">{`
from nukki_core import NukkiEngine
from nukki_core.fastapi import install

engine = NukkiEngine()
engine.start_load()                 # 백그라운드 로드. 포트는 즉시 열린다
install(app, engine, prefix="/v1")  # 기존 FastAPI 앱에 그대로 얹는다
`}</Code>
          <p className="note">
            we-together 처럼 이미 FastAPI 앱이 있으면 서버를 새로 띄울 필요가 없다.
            핸들러가 <code>async def</code> 가 아니라서 2~5초짜리 추론이 기존 API 를 막지 않는다.
          </p>
        </Section>

        <Section id="demo" title="데모" sub="아래는 이 페이지에서 실제로 도는 에디터다. 사진을 올려보라.">
          <div className="demo-wrap">
            <NukkiEditor backend={backend} showDownload />
          </div>
          <p className="note">
            <strong>영역 자동맞춤</strong>이나 올가미로 <strong>선택 영역</strong>을 잡고,
            추가·빼기로 다듬은 뒤 <strong>적용</strong>을 눌러야 잘린다 —
            올가미를 떼는 순간 이미지가 사라지지 않는다.
            올가미는 대충 감싸도 AI 가 경계를 맞추므로 손가락으로도 쓸 만하다.
            <kbd>Ctrl</kbd>+휠 확대, 두 손가락 핀치.
          </p>
        </Section>

        <Section
          id="skin"
          title="스키닝 4단계"
          sub="필요한 만큼만 내려가면 된다. 아래 넷은 전부 같은 컴포넌트이고, 오른쪽은 실제 렌더다."
        >
          <h3>1단계 — 통짜</h3>
          <Live code={`
<NukkiEditor
  backend={remoteBackend({ baseUrl: API })}
  onDone={(blob) => upload(blob)}
/>
`}>
            <AutoEditor backend={backend} workingSize={260} />
          </Live>

          <h3>2단계 — CSS 변수 테마</h3>
          <Live
            code={`
<NukkiEditor
  style={{
    '--nk-accent': '#f0a35e',
    '--nk-accent-strong': '#e0873a',
    '--nk-panel2': '#2b2620',
    '--nk-radius': '4px',
  }}
  … />
`}
            note="색·모서리만 바꾼다. 마크업은 그대로다."
          >
            <AutoEditor
              backend={backend}
              workingSize={260}
              style={{
                ['--nk-accent' as never]: '#f0a35e',
                ['--nk-accent-strong' as never]: '#e0873a',
                ['--nk-panel2' as never]: '#2b2620',
                ['--nk-radius' as never]: '4px',
              }}
            />
          </Live>

          <h3>3단계 — 슬롯에 클래스 꽂기</h3>
          <Live
            code={`
<NukkiEditor
  classNames={{
    toolbar: 'my-toolbar',
    button:  'my-btn',
    primary: 'my-btn-primary',
    stage:   'my-stage',
  }}
  lang="en"
  messages={{ auto: 'Magic cut' }}
  … />
`}
            note={
              '그냥 문자열이라 Tailwind v3·v4 어디서든 동작한다. 기본 클래스는 지워지지 않고 덧붙는다. ' +
              'lang·messages 로 문구도 바꾼다.'
            }
          >
            <AutoEditor
              backend={backend}
              workingSize={260}
              lang="en"
              messages={{ auto: 'Magic cut' }}
              classNames={{
                toolbar: 'demo-toolbar',
                button: 'demo-btn',
                primary: 'demo-btn-primary',
                stage: 'demo-stage',
              }}
            />
          </Live>

          <h3>4단계 — headless (완전 커스텀 UI)</h3>
          <Live
            code={`
const nk = useNukki({ backend });

return (
  <>
    <MyToolbar
      onAuto={nk.autoSelect} busy={nk.busy}
      canUndo={nk.canUndo} onUndo={nk.undo}
    />
    <NukkiCanvas nukki={nk} />
  </>
);
`}
            note="기본 툴바가 전혀 없다. 엔진 수명주기·포인터·제스처·렌더 루프만 훅이 맡는다."
          >
            <CustomUi />
          </Live>
        </Section>

        <Section id="rn" title="React Native" sub="canvas 가 없으므로 서버가 잘라준 결과를 받는다.">
          <Code>{`
import { NukkiClient } from '@nukki/client';

const nukki = new NukkiClient({
  baseUrl: NUKKI_BASE_URL,
  headers: { 'X-Nukki-Key': NUKKI_APP_KEY },
});

// RN FormData 가 받는 파일 서술자를 그대로 넘긴다 (플랫폼 분기 없음)
const photos = await pickPhotos();          // { uri, name, type }[]

const results = await nukki.cutoutBatch(photos, {
  concurrency: 2,                            // 서버 세마포어와 짝
  onProgress: ({ done, total }) => setProgress(done / total),
  onItem: (r) => r.ok && addSticker(r.blob),
});

// 저장 — 파일은 서버, DB 에는 상대 경로만
const { path, size, width, height, format } = await nukki.saveSticker(blob, ownerId);
<Image source={{ uri: nukki.stickerUrl(path) }} />
`}</Code>
          <div className="callout">
            <strong>배치가 핵심이다.</strong> 5장이면 서버에서 2~5초 × 5 = 10~25초다.
            한 장씩 기다리게 하면 기능이 죽는다. 화면을 먼저 열고 스티커가 하나씩
            채워지게 할 것. 개별 실패는 나머지를 중단시키지 않는다.
          </div>
          <div className="callout warn">
            <strong>저장 형식을 가정하지 말 것.</strong> 서버가 PNG·WebP 로 인코딩해
            <strong> 작은 쪽</strong>을 남긴다 — PNG 는 평면적 이미지에서, WebP 는 사진에서 강하다.
            응답의 <code>path</code> 와 <code>format</code> 을 그대로 저장할 것.
          </div>
        </Section>

        <Section id="api" title="서버 API">
          <table className="grid">
            <thead><tr><th>메서드</th><th>경로</th><th>설명</th></tr></thead>
            <tbody>
              <tr><td><code>GET</code></td><td><code>/v1/health</code></td>
                  <td><code>{'{ok, ready, model, error}'}</code> — <code>ready</code> 는 워밍업 실제 결과</td></tr>
              <tr><td><code>POST</code></td><td><code>/v1/remove</code></td>
                  <td>multipart <code>file</code> → 투명 PNG</td></tr>
              <tr><td><code>POST</code></td><td><code>/v1/mask</code></td>
                  <td><strong>RGBA PNG, 알파=마스크.</strong> 클라이언트가 <code>getImageData</code> 없이 쓰도록</td></tr>
              <tr><td><code>POST</code></td><td><code>/v1/stickers</code></td>
                  <td><code>file</code> + <code>owner</code> → <code>{'{path,size,width,height,format}'}</code>. 키 필요</td></tr>
              <tr><td><code>DELETE</code></td><td><code>/v1/stickers/{'{path}'}</code></td>
                  <td>키 필요. 없는 파일도 성공 (재시도 안전)</td></tr>
              <tr><td><code>GET</code></td><td><code>/v1/stickers/{'{path}'}</code></td>
                  <td>정적 서빙. 키 불필요. 영구 캐시 헤더</td></tr>
              <tr><td><code>GET</code></td><td><code>/v1/stickers/usage</code></td>
                  <td>용량 집계. 키 필요</td></tr>
            </tbody>
          </table>
          <div className="callout">
            <strong>키가 앱을 결정한다.</strong> <code>X-Nukki-Key</code> 로 앱 프리픽스를
            서버가 정하므로, 클라이언트가 남의 네임스페이스(<code>stickers/wt/…</code>)에
            쓰거나 지울 수 없다. 경로는 <code>stickers/{'{app}'}/{'{owner}'}/{'{uuid4}'}.{'{webp|png}'}</code>
            형태로 강제되고, 어긋나면 400 이다.
          </div>
        </Section>

        <Section id="deploy" title="배포">
          <Code lang="bash">{`
# 서버
pip install -r requirements.txt
NUKKI_STICKER_ROOT=/data/nukki \\
NUKKI_APP_KEYS=cp:<시크릿>,wt:<시크릿> \\
NUKKI_ALLOW_ORIGINS=https://app.example.com \\
uvicorn main:app --host 0.0.0.0 --port 8000
`}</Code>
          <table className="grid">
            <thead><tr><th>환경변수</th><th>기본값</th><th>비고</th></tr></thead>
            <tbody>
              <tr><td><code>REMBG_MODEL</code></td><td><code>isnet-general-use</code></td>
                  <td><code>__fake__</code> 면 모델 없이 동작 (UI 개발용)</td></tr>
              <tr><td><code>NUKKI_STICKER_ROOT</code></td><td>(없음)</td>
                  <td><strong>비우면 스티커 저장이 꺼진다</strong></td></tr>
              <tr><td><code>NUKKI_APP_KEYS</code></td><td>(없음)</td>
                  <td><code>cp:시크릿,wt:시크릿</code>. 비면 무인증 + 경고 로그</td></tr>
              <tr><td><code>NUKKI_MAX_CONCURRENCY</code></td><td>2</td>
                  <td><strong>RAM 에 맞출 것.</strong> 2GB 박스면 1~2</td></tr>
              <tr><td><code>NUKKI_ALLOW_ORIGINS</code></td><td><code>*</code></td>
                  <td>브라우저 소비처 도메인. <strong>web PWA 를 빠뜨리지 말 것</strong></td></tr>
            </tbody>
          </table>
          <div className="callout warn">
            <strong>모델은 백그라운드로 로드된다.</strong> 포트는 즉시 열리고
            그동안 <code>503 + Retry-After</code> 를 준다. <code>@nukki/client</code> 는
            이걸 자동 재시도하므로 배포 직후 요청도 통과한다.
          </div>
        </Section>
      </main>

      <footer>
        <p>
          <code>@nukki/core</code> · <code>@nukki/react</code> · <code>@nukki/client</code> ·
          <code> nukki-core</code> (pip) — MIT
        </p>
      </footer>
    </>
  );
}

/** 4단계 예시: 기본 툴바 없이 훅만으로. */
function CustomUi() {
  const nk = useNukki({ backend, workingSize: 260 });
  useEffect(() => { void sampleFile().then((f) => nk.load(f)); /* eslint-disable-line */ }, []);
  return (
    <div className="custom">
      <div className="custom-bar">
        <button onClick={() => void nk.autoSelect()} disabled={nk.busy}>
          {nk.busy ? '분석 중…' : '영역 잡기'}
        </button>
        <button onClick={nk.applySelection} disabled={!nk.hasSelection}>✂ 적용</button>
        <button onClick={nk.undo} disabled={!nk.canUndo}>↩</button>
        <button onClick={nk.redo} disabled={!nk.canRedo}>↪</button>
        <span className="custom-tool">
          {(['replace', 'add', 'subtract'] as const).map((m) => (
            <button key={m} data-on={nk.lassoMode === m} onClick={() => nk.setLassoMode(m)}>
              {{ replace: '새 영역', add: '추가', subtract: '빼기' }[m]}
            </button>
          ))}
        </span>
      </div>
      <NukkiCanvas nukki={nk} />
    </div>
  );
}

createRoot(document.getElementById('root')!).render(<App />);
